if (!customElements.get("pq-scrolling-promotion")) {
  class PQScrollingPromotion extends HTMLElement {
    constructor() {
      super();

      // 绑定this上下文
      this.animate = this.animate.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

      // 初始化状态
      this.isVisible = true;
      this.isHovered = false;
      this.animationId = null;
      this.currentPosition = 0;
      this.lastTimestamp = 0;

      // 配置参数
      this.direction = this.dataset.direction || "right";
      this.speed = parseInt(this.dataset.speed) || 3;
      this.pauseOnHover = this.dataset.pauseOnHover === "true";
      this.backgroundColor = this.dataset.backgroundColor || "#000000";
      this.textColor = this.dataset.textColor || "#ffffff";
      this.itemSpacing = parseInt(this.dataset.itemSpacing) || 50; // 新增：项目间距，默认50px

      // 动画参数
      this.animationSpeed = this.calculateAnimationSpeed();
      this.originalItemsWidth = 0;
      this.containerWidth = 0;
      this.originalItemsCount = 0;
      this.totalContentWidth = 0; // 包含复制内容的总宽度
    }

    connectedCallback() {
      // 等待DOM加载完成
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
          this.init();
        });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    // 初始化
    init() {
      // 获取DOM元素
      this.container = this.querySelector(".pq-scrolling-promotion__container");
      this.track = this.querySelector(".pq-scrolling-promotion__track");

      if (!this.container || !this.track) {
        console.error("PQScrollingPromotion: 缺少必要的DOM元素");
        return;
      }

      // 获取原始items
      this.originalItems = Array.from(
        this.track.querySelectorAll(".pq-scrolling-promotion__item")
      );
      this.originalItemsCount = this.originalItems.length;

      if (this.originalItemsCount === 0) {
        console.warn("PQScrollingPromotion: 没有找到促销项目");
        return;
      }

      // 设置初始样式
      this.setInitialStyles();

      // 绑定事件
      this.bindEvents();

      // 设置观察器
      this.setupObservers();

      // 初始化动画
      this.initAnimation();
    }

    // 设置初始样式
    setInitialStyles() {
      // 设置CSS变量
      this.style.setProperty("--background-color", this.backgroundColor);
      this.style.setProperty("--text-color", this.textColor);
      this.style.setProperty("--item-spacing", `${this.itemSpacing}px`); // 新增：设置项目间距CSS变量

      // 确保内容不换行
      this.track.style.whiteSpace = "nowrap";

      // 确保所有items都不换行
      this.originalItems.forEach((item) => {
        item.style.whiteSpace = "nowrap";
        item.style.padding = "0"; // 移除padding
        item.style.marginRight = `var(--item-spacing)`; // 使用间距作为右边距
      });
    }

    // 计算动画速度
    calculateAnimationSpeed() {
      // 基础速度：每秒移动的像素数
      const baseSpeed = 50;
      return baseSpeed * (this.speed / 3);
    }

    // 初始化动画
    initAnimation() {
      // 等待下一帧，确保DOM已渲染
      requestAnimationFrame(() => {
        this.measureAndSetup();
        this.startAnimation();
      });
    }

    // 测量尺寸并设置
    measureAndSetup() {
      this.containerWidth = this.container.offsetWidth;
      this.originalItemsWidth = this.calculateOriginalItemsWidth();

      // 设置无限滚动
      this.setupInfiniteScroll();

      // 重新计算动画速度
      this.animationSpeed = this.calculateAnimationSpeed();
    }

    // 计算原始items的总宽度
    calculateOriginalItemsWidth() {
      let totalWidth = 0;
      this.originalItems.forEach((item) => {
        totalWidth += item.offsetWidth + this.itemSpacing; // 考虑item间距
      });
      return totalWidth;
    }

    // 设置无限滚动
    setupInfiniteScroll() {
      // 移除之前可能存在的复制内容
      this.removeDuplicateItems();

      // 无论内容宽度如何，都至少复制两次原始内容，确保无缝滚动

      // 复制两次原始内容
      for (let i = 0; i < 2; i++) {
        this.originalItems.forEach((item) => {
          const duplicate = item.cloneNode(true);
          duplicate.classList.add("pq-scrolling-promotion__item--duplicate");
          this.track.appendChild(duplicate);
        });
      }

      // 重新计算总内容宽度
      this.totalContentWidth = this.originalItemsWidth * 3; // 原始 + 两份复制

      // 设置初始位置 - 修正左滚动的初始位置
      if (this.direction === "right") {
        this.currentPosition = 0;
      } else {
        // 向左滚动时，从第一个复制组的起始位置开始，这样可以向左滚动时无缝衔接
        this.currentPosition = 0;
      }

      // 立即应用初始位置
      this.track.style.transform = `translate3d(${this.currentPosition}px, 0, 0)`;
    }

    // 移除复制内容
    removeDuplicateItems() {
      const duplicates = this.track.querySelectorAll(
        ".pq-scrolling-promotion__item--duplicate"
      );
      duplicates.forEach((duplicate) => duplicate.remove());
    }

    // 开始动画
    startAnimation() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }

      this.lastTimestamp = performance.now();
      this.animate();
    }

    // 动画循环 - 修改版本以修复左滚动连接问题
    animate(timestamp) {
      // 如果页面不可见或悬停暂停，跳过计算但继续循环
      if (!this.isVisible || (this.isHovered && this.pauseOnHover)) {
        this.lastTimestamp = timestamp;
        this.animationId = requestAnimationFrame(this.animate);
        return;
      }

      const deltaTime = timestamp - this.lastTimestamp;
      this.lastTimestamp = timestamp;

      // 计算移动距离
      const moveDistance = (this.animationSpeed * deltaTime) / 1000;

      // 根据方向更新位置
      if (this.direction === "right") {
        this.currentPosition -= moveDistance;
      } else {
        this.currentPosition += moveDistance;
      }

      // 无缝循环逻辑 - 修正左滚动逻辑
      if (this.direction === "right") {
        // 向右滚动（内容向左移动）
        // 当滚动超过一组内容宽度时，跳回到合适位置
        if (this.currentPosition <= -this.originalItemsWidth) {
          // 将位置调整一个内容宽度的距离
          this.currentPosition += this.originalItemsWidth;
        }
      } else {
        // 向左滚动（内容向右移动）
        // 当滚动超过一组内容宽度时，跳回到合适位置
        if (this.currentPosition >= 0) {
          // 将位置调整回负的一个内容宽度
          this.currentPosition -= this.originalItemsWidth;
        }
      }

      // 应用transform
      this.track.style.transform = `translate3d(${this.currentPosition}px, 0, 0)`;

      // 继续动画
      this.animationId = requestAnimationFrame(this.animate);
    }

    // 暂停动画
    pauseAnimation() {
      this.isHovered = true;
    }

    // 恢复动画
    resumeAnimation() {
      this.isHovered = false;
    }

    // 停止动画
    stopAnimation() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
    }

    // 绑定事件
    bindEvents() {
      // 窗口调整大小
      window.addEventListener("resize", this.handleResize);

      // 页面可见性变化
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );

      // 鼠标悬停事件
      if (this.pauseOnHover) {
        this.addEventListener("mouseenter", () => this.pauseAnimation());
        this.addEventListener("mouseleave", () => this.resumeAnimation());
      }

      // 触摸事件（移动设备）
      if ("ontouchstart" in window) {
        this.addEventListener("touchstart", () => this.pauseAnimation());
        this.addEventListener("touchend", () => this.resumeAnimation());
      }
    }

    // 设置观察器
    setupObservers() {
      // Intersection Observer - 观察元素是否在视口中
      if ("IntersectionObserver" in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isVisible = entry.isIntersecting;
            });
          },
          {
            threshold: 0.1,
          }
        );

        this.intersectionObserver.observe(this);
      }

      // Resize Observer - 观察尺寸变化
      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.handleResize();
        });

        this.resizeObserver.observe(this.container);
      }
    }

    // 处理窗口大小变化
    handleResize() {
      // 防抖处理
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        // 保存当前方向
        const currentDirection = this.direction;

        // 重新测量和设置
        this.measureAndSetup();

        // 根据方向设置正确的位置
        if (currentDirection === "right") {
          this.currentPosition = 0;
        } else {
          this.currentPosition = -this.originalItemsWidth;
        }

        // 应用新位置
        this.track.style.transform = `translate3d(${this.currentPosition}px, 0, 0)`;
      }, 250);
    }

    // 处理页面可见性变化
    handleVisibilityChange() {
      this.isVisible = !document.hidden;
    }

    // 设置方向
    setDirection(direction) {
      this.direction = direction;
      this.dataset.direction = direction;

      // 根据新方向设置正确的初始位置 - 修正左滚动初始位置
      if (this.direction === "right") {
        this.currentPosition = 0;
      } else {
        // 向左滚动起始位置修正
        this.currentPosition = -this.originalItemsWidth;
      }

      // 立即应用新位置
      if (this.track) {
        this.track.style.transform = `translate3d(${this.currentPosition}px, 0, 0)`;
      }
    }

    // 设置速度
    setSpeed(speed) {
      this.speed = parseInt(speed);
      this.dataset.speed = speed;
      this.animationSpeed = this.calculateAnimationSpeed();
    }

    // 设置悬停暂停
    setPauseOnHover(pause) {
      this.pauseOnHover = pause;
      this.dataset.pauseOnHover = pause.toString();

      // 重新绑定事件
      this.removeEventListener("mouseenter", this.pauseAnimation);
      this.removeEventListener("mouseleave", this.resumeAnimation);

      if (pause) {
        this.addEventListener("mouseenter", () => this.pauseAnimation());
        this.addEventListener("mouseleave", () => this.resumeAnimation());
      }
    }

    // 更新内容
    updateContent() {
      // 重新获取原始items
      this.originalItems = Array.from(
        this.track.querySelectorAll(
          ".pq-scrolling-promotion__item:not(.pq-scrolling-promotion__item--duplicate)"
        )
      );
      this.originalItemsCount = this.originalItems.length;

      // 重新设置无限滚动
      this.setupInfiniteScroll();

      // 重置位置
      this.currentPosition = 0;
    }

    // 检查是否需要复制内容
    needsDuplication() {
      const minRequiredWidth = this.containerWidth * 1.5;
      return this.originalItemsWidth < minRequiredWidth;
    }

    // 获取复制的数量
    getDuplicateCount() {
      return this.track.querySelectorAll(
        ".pq-scrolling-promotion__item--duplicate"
      ).length;
    }

    // 清理资源
    cleanup() {
      // 移除事件监听器
      window.removeEventListener("resize", this.handleResize);
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );

      // 清理观察器
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      // 停止动画
      this.stopAnimation();

      // 清理定时器
      clearTimeout(this.resizeTimeout);
    }

    // 重新初始化
    reinit() {
      this.cleanup();
      this.init();
    }

    // 获取当前状态
    getState() {
      return {
        isVisible: this.isVisible,
        isHovered: this.isHovered,
        direction: this.direction,
        speed: this.speed,
        pauseOnHover: this.pauseOnHover,
        animationSpeed: this.animationSpeed,
        currentPosition: this.currentPosition,
        containerWidth: this.containerWidth,
        originalItemsWidth: this.originalItemsWidth,
        totalContentWidth: this.totalContentWidth,
        originalItemsCount: this.originalItemsCount,
        duplicateCount: this.getDuplicateCount(),
        needsDuplication: this.needsDuplication(),
        animationRunning: this.animationId !== null,
      };
    }

    // 设置项目间距
    setItemSpacing(spacing) {
      this.itemSpacing = parseInt(spacing);
      this.dataset.itemSpacing = spacing;
      this.style.setProperty("--item-spacing", `${this.itemSpacing}px`);

      // 更新所有item的margin
      const allItems = this.track.querySelectorAll(
        ".pq-scrolling-promotion__item"
      );
      allItems.forEach((item) => {
        item.style.marginRight = `var(--item-spacing)`;
      });

      // 重新计算宽度和重置动画
      this.measureAndSetup();
    }

    // 设置配置
    setConfig(config) {
      if (config.direction) this.setDirection(config.direction);
      if (config.speed) this.setSpeed(config.speed);
      if (config.pauseOnHover !== undefined)
        this.setPauseOnHover(config.pauseOnHover);
      if (config.backgroundColor) {
        this.backgroundColor = config.backgroundColor;
        this.style.setProperty("--background-color", this.backgroundColor);
      }
      if (config.textColor) {
        this.textColor = config.textColor;
        this.style.setProperty("--text-color", this.textColor);
      }
      if (config.itemSpacing !== undefined)
        this.setItemSpacing(config.itemSpacing); // 新增：设置项目间距
    }
  }

  // 静态方法
  PQScrollingPromotion.getAll = function () {
    return Array.from(document.querySelectorAll("pq-scrolling-promotion"));
  };

  PQScrollingPromotion.pauseAll = function () {
    this.getAll().forEach((element) => {
      element.pauseAnimation();
    });
  };

  PQScrollingPromotion.resumeAll = function () {
    this.getAll().forEach((element) => {
      element.resumeAnimation();
    });
  };

  PQScrollingPromotion.stopAll = function () {
    this.getAll().forEach((element) => {
      element.stopAnimation();
    });
  };

  PQScrollingPromotion.reinitAll = function () {
    this.getAll().forEach((element) => {
      element.reinit();
    });
  };

  // 注册自定义元素
  customElements.define("pq-scrolling-promotion", PQScrollingPromotion);
}

// 页面加载完成后进行额外优化
document.addEventListener("DOMContentLoaded", () => {
  // 性能优化：在低端设备上降低动画频率
  const isLowEndDevice =
    navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2;

  if (isLowEndDevice) {
    // 降低动画频率
    const originalRAF = window.requestAnimationFrame;
    let throttle = 0;
    window.requestAnimationFrame = function (callback) {
      if (throttle++ % 2 === 0) {
        return originalRAF(callback);
      }
      return originalRAF(() => {});
    };
  }
});
